import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';

import { EmailClientService } from './email-client.service';
import { EmailMessageSummary } from './email-client.types';
import {
  EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION,
  FirebaseEvent,
  FirebaseService,
} from './firebase.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class EmailClientRealtimeService implements OnDestroy {
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly recentMessagesSubject = new BehaviorSubject<EmailMessageSummary[]>([]);
  private readonly messageReceivedSubject = new Subject<FirebaseEvent>();
  private readonly firebaseSubscription: Subscription;
  private activeAccessToken?: string;
  private refreshing = false;
  private refreshQueued = false;

  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly recentMessages$ = this.recentMessagesSubject.asObservable();
  readonly messageReceived$ = this.messageReceivedSubject.asObservable();

  constructor(
    private readonly emails: EmailClientService,
    private readonly firebase: FirebaseService,
    private readonly tokens: TokenService,
  ) {
    this.firebaseSubscription = this.firebase.events$.subscribe((event) => {
      if (event.notification_id !== EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION) return;
      this.messageReceivedSubject.next(event);
      void this.refreshUnreadCount();
    });
  }

  ngOnDestroy(): void {
    this.firebaseSubscription.unsubscribe();
  }

  start(): void {
    const accessToken = this.tokens.getAccessToken();
    if (!accessToken) {
      this.activeAccessToken = undefined;
      this.unreadCountSubject.next(0);
      this.recentMessagesSubject.next([]);
      return;
    }
    this.firebase.start(true);
    if (this.activeAccessToken === accessToken) return;
    this.activeAccessToken = accessToken;
    void this.refreshUnreadCount();
  }

  async refreshUnreadCount(): Promise<void> {
    if (!this.tokens.getAccessToken()) return;
    if (this.refreshing) {
      this.refreshQueued = true;
      return;
    }
    this.refreshing = true;
    try {
      do {
        this.refreshQueued = false;
        const [result, accounts] = await Promise.all([
          firstValueFrom(this.emails.unreadCount()),
          firstValueFrom(this.emails.listAccounts()),
        ]);
        this.unreadCountSubject.next(Math.max(0, Number(result.unread_count || 0)));
        const pages = await Promise.all(accounts.map((account) =>
          firstValueFrom(this.emails.listMessages({
            account_id: account.id,
            mailbox: 'inbox',
            page: 1,
            limit: 6,
          })),
        ));
        this.recentMessagesSubject.next(
          pages
            .flatMap((page) => page.records)
            .filter((message) => !message.is_read)
            .sort((left, right) => Date.parse(right.received_at) - Date.parse(left.received_at))
            .slice(0, 6),
        );
      } while (this.refreshQueued && this.tokens.getAccessToken());
    } catch {
      // Keep the last known count during a transient network or sync failure.
    } finally {
      this.refreshing = false;
    }
  }
}
